import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  AcademicCapIcon,
  BookOpenIcon,
  UserGroupIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';

const Layout = ({ children, userProfile, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const navigation = [
    { name: 'Overview', href: '/overview', icon: HomeIcon },
    { name: 'Students', href: '/students', icon: UsersIcon },
    { name: 'Batches', href: '/batches', icon: UserGroupIcon },
    { name: 'Courses', href: '/courses', icon: BookOpenIcon },
    { name: 'Materials', href: '/materials', icon: DocumentTextIcon },
    { name: 'Teachers', href: '/teachers', icon: AcademicCapIcon },
  ];

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  // Generate user initials from name or email
  const getUserInitials = () => {
    if (userProfile?.first_name && userProfile?.last_name) {
      return (userProfile.first_name[0] + userProfile.last_name[0]).toUpperCase();
    } else if (userProfile?.email) {
      return userProfile.email.substring(0, 2).toUpperCase();
    }
    return 'AD';
  };

  // Get display name
  const getDisplayName = () => {
    if (userProfile?.first_name || userProfile?.last_name) {
      return `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
    }
    return userProfile?.username || userProfile?.email || 'Admin';
  };

  const handleNavigation = (href) => {
    navigate(href);
    setSidebarOpen(false);
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/20">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300" 
            onClick={() => setSidebarOpen(false)} 
          />
        </div>
      )}

      {/* Sidebar */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed inset-y-0 left-0 z-50 w-72 bg-gray-900 shadow-2xl shadow-black/10 border-r border-gray-800 transform transition-all duration-500 ease-out md:translate-x-0 md:static md:inset-0 ${isLoaded ? 'animate-in slide-in-from-left' : ''}`}>
        
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-20 px-6 bg-gray-900 relative overflow-hidden border-b border-gray-800">
          <div className="flex items-center gap-2 relative z-10">
            {!logoError ? (
              <>
                <img 
                  src="/images/aitech-light.png" 
                  alt="Aitech Logo" 
                  className="h-6 w-auto filter brightness-0 invert"
                  onError={() => setLogoError(true)}
                />
                <span className="text-white font-semibold text-sm tracking-tight">LMS Admin</span>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-white font-bold text-lg tracking-tight drop-shadow-sm">Aitech</h1>
                <span className="text-white font-semibold text-sm tracking-tight">LMS Admin</span>
              </div>
            )}
          </div>
          <button
            className="md:hidden text-gray-400 hover:text-white hover:bg-gray-800 p-2 rounded-xl transition-all duration-200"
            onClick={() => setSidebarOpen(false)}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-8 px-2">
          <div className="space-y-2">
            {navigation.map((item, index) => {
              const isActive = location.pathname === item.href;
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className={`${
                    isActive
                      ? 'bg-gray-700 text-white border-transparent'
                      : 'text-gray-300 hover:text-white hover:bg-gray-800 border-transparent'
                  } group w-full text-left flex items-center px-2 py-2 text-sm font-medium rounded-xl transition-all duration-300 ease-out border transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 focus:ring-offset-gray-900`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <item.icon className={`mr-3 h-5 w-5 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                  <span className="font-semibold">{item.name}</span>
                  {isActive && (
                    <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Sidebar footer */}
        <div className="absolute bottom-0 w-full p-4">
          <div className="border-t border-gray-800 pt-4">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-gray-300 hover:text-red-400 hover:bg-gray-800 rounded-xl transition-all duration-300 ease-out group transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:ring-offset-2 focus:ring-offset-gray-900"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white/80 backdrop-blur-xl shadow-sm border-b border-white/20 flex-shrink-0 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-white/60 via-white/80 to-white/60"></div>
          <div className="relative flex items-center justify-between h-20 px-6 lg:px-8">
            <div className="flex items-center">
              <button
                className="md:hidden text-gray-500 hover:text-gray-700 hover:bg-gray-100/80 p-2 rounded-xl mr-3 transition-all duration-200"
                onClick={() => setSidebarOpen(true)}
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              
              <div className="flex items-center">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent capitalize tracking-tight">
                  {location.pathname.slice(1) || 'overview'}
                </h2>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              {/* Search */}
              <div className="hidden sm:block">
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors duration-200" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search anything..."
                    className="block w-96 pl-12 pr-4 py-3 border border-gray-200/60 rounded-2xl bg-white/70 backdrop-blur-sm text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/40 focus:bg-white transition-all duration-300 shadow-sm hover:shadow-md hover:bg-white/90"
                  />
                </div>
              </div>

              {/* Notifications */}
              <button className="relative p-3 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50/80 rounded-2xl transition-all duration-300 group">
                <BellIcon className="h-6 w-6 group-hover:scale-110 transition-transform duration-300" />
                <span className="absolute -top-1 -right-1 block h-5 w-5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="block h-2 w-2 bg-white rounded-full animate-pulse"></span>
                </span>
              </button>

              {/* Profile dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center justify-center focus:outline-none"
                >
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white text-sm font-bold">{getUserInitials()}</span>
                  </div>
                </button>

                {profileOpen && (
                  <div className="absolute right-0 mt-3 w-64 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-black/10 border border-white/20 py-2 z-20 animate-in slide-in-from-top-2 duration-300">
                    <div className="px-6 py-4 border-b border-gray-100/60">
                      <p className="text-sm font-bold text-gray-900">{getDisplayName()}</p>
                      <p className="text-sm text-gray-500 mt-1">{userProfile?.email || 'No email'}</p>
                      <div className="mt-3 px-3 py-1.5 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full inline-flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                        <span className="text-xs font-medium text-green-700">Online</span>
                      </div>
                    </div>
                    <div className="py-2">
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setProfileOpen(false);
                        }}
                        className="w-full text-left px-6 py-3 text-sm font-medium text-gray-700 hover:text-indigo-600 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 transition-all duration-200 flex items-center group"
                      >
                        <UserCircleIcon className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                        Profile Settings
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-6 py-3 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-200 flex items-center group"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50/50 via-blue-50/20 to-indigo-50/10">
          <div className="p-8 h-full">
            <div className={`transition-all duration-700 ${isLoaded ? 'animate-in fade-in slide-in-from-bottom-4' : 'opacity-0'}`}>
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 